import { createContext, useContext } from 'react';
import type { TourId } from './types';

export interface TourApi {
  /** Restarts the named tour from step one, wherever the user is standing. */
  start: (tourId: TourId) => void;
  stop: () => void;
  running: boolean;
  tourId: TourId | null;
}

export const TourContext = createContext<TourApi | null>(null);

export function useTour(): TourApi {
  const context = useContext(TourContext);
  if (!context) throw new Error('useTour must be used inside TourProvider');
  return context;
}

/**
 * Whether this person has been offered the tour.
 *
 * The answer belongs to the USER and lives on their record, because "once" means once for a
 * person: not once per browser, not once per device, and not until they clear site data.
 * The key below is only a same-session guard so a slow round trip cannot show the prompt
 * twice; it is never the source of truth, and a browser that has never seen this person
 * still defers to what the server says.
 *
 * Tour progress itself is never stored: a tour always starts at step one.
 */
export const TOUR_SEEN_KEY = 'tabfile.tourSeen';

export function hasSeenTourPromptLocally(): boolean {
  try {
    return localStorage.getItem(TOUR_SEEN_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Records the offer, on the user first and in this browser second.
 *
 * Called the moment the prompt is SHOWN, not when it is dismissed. A prompt that sat on
 * screen and timed out has still been seen, and counting only a dismissal as an answer is
 * exactly why it kept coming back. Failure is swallowed: being asked twice about a tour is a
 * small thing, and an error message about it would be a larger one.
 */
export async function markTourPromptSeen(): Promise<void> {
  try {
    localStorage.setItem(TOUR_SEEN_KEY, '1');
  } catch {
    // Storage is off. The server record still carries the answer.
  }
  try {
    const { authStore } = await import('@/lib/auth-store');
    const { env } = await import('@/lib/env');
    const token = await authStore.getToken();
    if (!token) return;
    await fetch(`${env.apiBaseUrl}/v1/me/tour-prompt-seen`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    // Offline, or the API is restarting. The local flag covers this session.
  }
}
